/**
 * Volume-based bar/candle width helpers.
 * Change `avgPeriodDays` (e.g. 30, 60, 252) to switch the rolling average window.
 */

export type VolumeWidthConfig = {
  /** Rolling lookback in trading days (e.g. 30, 60, 252) */
  avgPeriodDays: number;
  /** Width at volume === rolling average (fraction of category band) */
  baseWidthRatio: number;
  /** Min width when volume is below average */
  minWidthRatio: number;
  /** Max width when volume is well above average */
  maxWidthRatio: number;
};

/** User-selectable rolling volume windows (days). */
export const VOLUME_AVG_PERIOD_OPTIONS = [10, 20, 50, 100, 200] as const;

export type VolumeAvgPeriodOption = (typeof VOLUME_AVG_PERIOD_OPTIONS)[number];

export const DEFAULT_VOLUME_AVG_PERIOD: VolumeAvgPeriodOption = 20;

/** ECharts legend label for volume-scaled candle bodies. */
export const VOL_BASED_CANDLE_LEGEND_NAME = "Vol. width";

export function isVolumeAvgPeriodOption(
  value: number,
): value is VolumeAvgPeriodOption {
  return (VOLUME_AVG_PERIOD_OPTIONS as readonly number[]).includes(value);
}

export function normalizeVolumeAvgPeriodDays(
  days?: number,
  fallback: VolumeAvgPeriodOption = DEFAULT_VOLUME_AVG_PERIOD,
): VolumeAvgPeriodOption {
  if (days != null && isVolumeAvgPeriodOption(days)) {
    return days;
  }
  return fallback;
}

export const DEFAULT_VOLUME_WIDTH_CONFIG: VolumeWidthConfig = {
  avgPeriodDays: DEFAULT_VOLUME_AVG_PERIOD,
  baseWidthRatio: 0.55,
  minWidthRatio: 0.2,
  maxWidthRatio: 1,
};

export function mergeVolumeWidthConfig(
  partial?: Partial<VolumeWidthConfig>,
): VolumeWidthConfig {
  return { ...DEFAULT_VOLUME_WIDTH_CONFIG, ...partial };
}

/** Rolling mean volume for each index (uses prior `periodDays` bars inclusive). */
export function computeRollingAverageVolumes(
  volumes: number[],
  periodDays: number,
): number[] {
  const period = Math.max(1, periodDays);
  const result: number[] = [];
  let sum = 0;

  for (let i = 0; i < volumes.length; i++) {
    const vol = Number(volumes[i]) || 0;
    sum += vol;

    if (i >= period) {
      sum -= Number(volumes[i - period]) || 0;
      result.push(sum / period);
    } else {
      result.push(sum / (i + 1));
    }
  }
  return result;
}

/**
 * Width ratio from volume vs rolling average: 1 = average, >1 = heavier volume.
 * Uses vol/avg so changing the period automatically rescales all widths.
 */
export function computeVolumeToAvgRatios(
  volumes: number[],
  avgVolumes: number[],
): number[] {
  return volumes.map((volume, index) => {
    const avg = avgVolumes[index];
    const vol = Number(volume) || 0;
    if (!avg || avg <= 0) return 1;
    return vol / avg;
  });
}

export function volumeRatioToWidthRatio(
  volumeToAvgRatio: number,
  config: VolumeWidthConfig,
): number {
  const scaled = config.baseWidthRatio * volumeToAvgRatio;
  return Math.min(config.maxWidthRatio, Math.max(config.minWidthRatio, scaled));
}

export function computeVolumeWidthRatios(
  volumes: number[],
  config: VolumeWidthConfig = DEFAULT_VOLUME_WIDTH_CONFIG,
): number[] {
  const avgVolumes = computeRollingAverageVolumes(
    volumes,
    config.avgPeriodDays,
  );
  const volToAvg = computeVolumeToAvgRatios(volumes, avgVolumes);
  return volToAvg.map((ratio) => volumeRatioToWidthRatio(ratio, config));
}

type CustomRenderParams = {
  dataIndex: number;
  dataIndexInside: number;
};

type CustomRenderApi = {
  value: (dim: number) => unknown;
  coord: (point: [number, number]) => [number, number];
  size: (size: [number, number]) => [number, number];
  style: (style?: Record<string, unknown>) => Record<string, unknown>;
};

const BULL_COLOR = "#089981";
const BEAR_COLOR = "#FD1050";

/** Keep bar/candle bodies inside their category band (avoids margin overflow & tooltip misses). */
export function clampHorizontalRectToBand(
  centerX: number,
  bandWidth: number,
  desiredWidth: number,
): { x: number; width: number } {
  const maxWidth = Math.max(1, bandWidth - 1);
  const width = Math.min(Math.max(1, desiredWidth), maxWidth);
  const bandLeft = centerX - bandWidth / 2;
  const bandRight = centerX + bandWidth / 2;
  let x = centerX - width / 2;
  if (x < bandLeft) {
    x = bandLeft;
  }
  const overflowRight = x + width - bandRight;
  if (overflowRight > 0) {
    x -= overflowRight;
  }
  return { x, width };
}

export function createVolumeCandleRenderItem(options: {
  getWidthRatio: (dataIndex: number) => number;
  isTodayCandle: (dataIndex: number) => boolean;
  isBullishCandle: (dataIndex: number) => boolean;
  isRenderableAtIndex?: (dataIndex: number) => boolean;
}) {
  const { getWidthRatio, isTodayCandle, isBullishCandle, isRenderableAtIndex } =
    options;

  return function renderItem(params: CustomRenderParams, api: CustomRenderApi) {
    const dataIndex = params.dataIndex;
    if (isRenderableAtIndex && !isRenderableAtIndex(dataIndex)) {
      return;
    }
    const open = Number(api.value(0));
    const close = Number(api.value(1));
    const low = Number(api.value(2));
    const high = Number(api.value(3));

    if (
      params.dataIndexInside < 0 ||
      [open, close, low, high].some((v) => Number.isNaN(v))
    ) {
      return;
    }

    const widthRatio = getWidthRatio(dataIndex);
    const bandWidth = api.size([1, 0])[0];
    const centerX = api.coord([dataIndex, close])[0];
    const { x: bodyX, width: bodyWidth } = clampHorizontalRectToBand(
      centerX,
      bandWidth,
      bandWidth * widthRatio,
    );
    const openPoint = api.coord([dataIndex, open]);
    const closePoint = api.coord([dataIndex, close]);
    const lowPoint = api.coord([dataIndex, low]);
    const highPoint = api.coord([dataIndex, high]);

    const isBullish = isBullishCandle(dataIndex);
    const isToday = isTodayCandle(dataIndex);
    const color = isBullish ? BULL_COLOR : BEAR_COLOR;

    const bodyTop = Math.min(openPoint[1], closePoint[1]);
    const bodyBottom = Math.max(openPoint[1], closePoint[1]);
    const bodyHeight = Math.max(bodyBottom - bodyTop, 1);

    const style = api.style({
      fill: color,
      stroke: color,
      lineWidth: isToday ? 2 : 1,
      shadowBlur: isToday ? 10 : 0,
      shadowColor: isToday ? color : undefined,
    });

    return {
      type: "group",
      children: [
        {
          type: "line",
          shape: {
            x1: highPoint[0],
            y1: highPoint[1],
            x2: lowPoint[0],
            y2: lowPoint[1],
          },
          style: {
            stroke: color,
            lineWidth: 1,
          },
        },
        {
          type: "rect",
          shape: {
            x: bodyX,
            y: bodyTop,
            width: bodyWidth,
            height: bodyHeight,
          },
          style,
        },
      ],
    };
  };
}

export function createVolumeBarRenderItem(options: {
  getWidthRatio: (dataIndex: number) => number;
  getBarColor: (dataIndex: number) => string;
  isRenderableAtIndex?: (dataIndex: number) => boolean;
}) {
  const { getWidthRatio, getBarColor, isRenderableAtIndex } = options;

  return function renderItem(params: CustomRenderParams, api: CustomRenderApi) {
    const dataIndex = params.dataIndex;
    if (isRenderableAtIndex && !isRenderableAtIndex(dataIndex)) {
      return;
    }
    const volume = Number(api.value(0));

    if (params.dataIndexInside < 0 || Number.isNaN(volume)) {
      return;
    }

    const widthRatio = getWidthRatio(dataIndex);
    const bandWidth = api.size([1, 0])[0];
    const centerX = api.coord([dataIndex, volume])[0];
    const { x: barX, width: barWidth } = clampHorizontalRectToBand(
      centerX,
      bandWidth,
      bandWidth * widthRatio,
    );

    const basePoint = api.coord([dataIndex, 0]);
    const topPoint = api.coord([dataIndex, volume]);

    return {
      type: "rect",
      shape: {
        x: barX,
        y: topPoint[1],
        width: barWidth,
        height: basePoint[1] - topPoint[1],
      },
      style: api.style({
        fill: getBarColor(dataIndex),
        opacity: 0.6,
      }),
    };
  };
}
